/** 이름 후보 점검 — a candidate name judged before it goes into App Store
 *  Connect: the character limits, the words spent twice, and for each phrase
 *  where it stands, its demand and who holds it at the top of the store. Each
 *  phrase is two store calls, so only the button runs it. */

import { useState, type FormEvent } from 'react'
import { useMutation } from '@tanstack/react-query'
import { SearchCheck } from 'lucide-react'
import { api, type NameCheckPhrase, type NameCheckResult } from '@/api/client'
import { demandLine } from '@/lib/demand'
import { TONE_CLASS } from '@/lib/status'
import { cn } from '@/lib/utils'
import { Button, DataTable, EmptyState, errorText, Field, Input, useToast, type Column } from '@/ui'

const WHERE: Record<NameCheckPhrase['where'], string> = {
  name_start: '이름 맨 앞',
  name: '이름 뒤쪽',
  subtitle: '부제',
}

const COLUMNS: Column<NameCheckPhrase>[] = [
  { key: 'phrase', header: '문구', card: 'title', cell: (p) => <span className="font-medium">{p.phrase}</span> },
  { key: 'where', header: '위치', card: 'meta', className: 'whitespace-nowrap text-muted-foreground', cell: (p) => WHERE[p.where] },
  {
    key: 'pop',
    header: '인기도',
    align: 'right',
    card: 'line',
    className: 'tabular-nums',
    cell: (p) => (p.demand.popularity > 0 ? p.demand.popularity : <span className="text-muted-foreground">—</span>),
  },
  { key: 'demand', header: '수요', card: 'line', className: 'text-muted-foreground', cell: (p) => demandLine(p.demand) },
  {
    key: 'holders',
    header: '이름 점유',
    align: 'right',
    card: 'line',
    className: 'whitespace-nowrap tabular-nums',
    cell: (p) => (p.store ? `${p.store.name_holders}/${p.store.top.length}` : <span className="text-muted-foreground">—</span>),
  },
  {
    key: 'ratings',
    header: '평점 중앙값',
    align: 'right',
    card: 'line',
    className: 'whitespace-nowrap tabular-nums',
    cell: (p) => (p.store ? p.store.median_ratings.toLocaleString('ko-KR') : <span className="text-muted-foreground">—</span>),
  },
]

function Length({ label, used, limit }: { label: string; used: number; limit: number }) {
  return (
    <span className={cn('tabular-nums', used > limit ? TONE_CLASS.failed.text : 'text-muted-foreground')}>
      {label} {used}/{limit}
    </span>
  )
}

function Outcome({ result }: { result: NameCheckResult }) {
  return (
    <div className="space-y-3">
      <p className="flex flex-wrap gap-x-4 gap-y-1 text-table">
        <Length label="이름" used={result.name_length} limit={result.name_limit} />
        <Length label="부제" used={result.subtitle_length} limit={result.subtitle_limit} />
        <Length label="키워드" used={result.keywords_length} limit={result.keywords_limit} />
      </p>
      <p className="text-table text-muted-foreground">
        {result.duplicates.length > 0 ? `겹치는 낱말: ${result.duplicates.join(', ')}` : '겹치는 낱말이 없다.'}
      </p>
      <DataTable
        label="이름 후보 문구"
        rows={result.phrases}
        rowKey={(p) => p.phrase}
        columns={COLUMNS}
        empty={<EmptyState title="문구를 뽑지 못했다" description="이름을 `문구 - 브랜드` 꼴로 넣는다." />}
      />
      {result.phrases.some((p) => p.store_error) && <p className="text-meta text-muted-foreground">스토어를 읽지 못한 문구는 값을 비워 둔다.</p>}
    </div>
  )
}

export function NameCheck({ country }: { country: string }) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [keywords, setKeywords] = useState('')
  const check = useMutation({
    mutationFn: () => api.nameCheck({ name: name.trim(), subtitle: subtitle.trim(), keywords: keywords.trim(), country }),
    onError: (e) => toast.error(errorText(e)),
  })
  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (name.trim() && !check.isPending) check.mutate()
  }
  return (
    <section aria-label="이름 후보 점검" className="mt-8 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <h2 className="text-section font-semibold">이름 후보 점검</h2>
        <span className="text-meta text-muted-foreground">{country.toUpperCase()} 스토어를 읽기만 한다. ASC에는 아무것도 쓰지 않는다</span>
      </div>
      <form onSubmit={submit} aria-label="이름 후보" className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-4">
        <Field label="후보 이름" className="min-w-[14rem] flex-1">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Photo Booth - Kkumi" />
        </Field>
        <Field label="후보 부제" className="min-w-[14rem] flex-1">
          <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
        </Field>
        <Field label="후보 키워드" hint="쉼표로 잇는다" className="w-full">
          <Input value={keywords} onChange={(e) => setKeywords(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" icon={SearchCheck} loading={check.isPending} disabled={!name.trim()}>
          {check.isPending ? '읽는 중' : '점검'}
        </Button>
      </form>
      {check.data && <Outcome result={check.data} />}
    </section>
  )
}
